import React, { useEffect, useRef, useState } from "react";
import styles from "./article-category.module.css";
import Loader from "../../../../components/Loader";
import { useAuth0 } from "@auth0/auth0-react";
import { Helmet } from "react-helmet";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Slide, Snackbar, TablePagination, Typography } from "@mui/material";
import { ArrowLeft, Home, X } from "react-feather";
import Footer from "../../../../components/Footer";
import ArticleCategoryCard from "./components/ArticleCategoryCard";
import ArticleCategoryFilter from "./components/ArticleCategoryFilter";
import { TableLoader } from "../../../../components/LoaderC";
import axios from "axios";
import ArticleCategoryAdd from "./components/ArticleCategoryAdd";
import { AddLogCallAPI } from "../../../../components/AddLogs";
import { toast } from 'react-smart-toaster';
import withCustomAuthenticationRequired from "../../../../auth/withCustomAuthenticationRequired";


const ArticleCategoryList = ({ history, match }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [loading, setLoading] = useState(false);
    const [contentLoading, setContentLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showdeletePopup, setShowdeletePopup] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState("");
    const [categoryList, setCategoryList] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loadError, setLoadError] = useState(false);
    const [searchKey, setSearchKey] = useState("");
    const [reloadKey, setReloadKey] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(8);
    const [filterData, setFilterData] = useState({ searchKey: "" });
    const [selectecId, setSelectecId] = useState(0);
    const latestRequest = useRef(0);

    // Debounce search so we don't hit the API on every keystroke
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchKey(filterData.searchKey);
            setPage(0);
        }, filterData.searchKey ? 400 : 0);
        return () => clearTimeout(timer);
    }, [filterData.searchKey]);

    useEffect(() => {
        const requestId = ++latestRequest.current;

        async function getCategories() {
            try {
                const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

                const response = await axios.get(process.env.REACT_APP_RESOURCE_API_URL + "v1/article-categories/paginated", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    params: {
                        page: page + 1,
                        per_page: perPage,
                        search: searchKey || undefined,
                    },
                });

                // Ignore responses from requests that were superseded by newer searches/pages
                if (requestId !== latestRequest.current) return;

                const rows = response.data?.data || [];
                const total = response.data?.pagination?.total || 0;

                // The last item on a page was deleted, so step back to the previous page
                if (rows.length === 0 && page > 0 && total > 0) {
                    setPage(Math.max(Math.ceil(total / perPage) - 1, 0));
                    return;
                }

                setCategoryList(rows);
                setTotalCount(total);
                setLoadError(false);
            } catch (error) {
                if (requestId !== latestRequest.current) return;
                console.error('[KnowledgeBaseCategory] Failed to load category list', error);
                setLoadError(true);
                toast.error(error?.response?.data?.message || 'Unable to load categories. Please try again.');
            } finally {
                if (requestId === latestRequest.current)
                    setContentLoading(false);
            }
        }
        getCategories();
    }, [getAccessTokenSilently, page, perPage, searchKey, reloadKey]);

    // Reload the first page so the new category shows at the top and the total stays accurate
    const onAdd = () => {
        setPage(0);
        setReloadKey(prev => prev + 1);
    }

    const confirmDelete = async () => {
        setShowdeletePopup(false);
        setLoading(true);
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        axios.delete(process.env.REACT_APP_RESOURCE_API_URL + 'v1/article-categories/' + selectecId, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then(response => {
            setSnackbarOpen(true);
            setSnackbarMsg(response.data.message);
            setLoading(false);

            // Reload from the server so the page refills and the total stays accurate
            setReloadKey(prev => prev + 1);
            setSelectecId(0);

            let logData = {
                'title': 'A KB Category is deleted',
                'description': [
                    'Title: ' + response.data.data?.title
                ]
            }

            AddLogCallAPI(logData, token);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    return (<React.Fragment>
        {loading && <Loader />}

        <Helmet title="Knowledge Base Category" />
        <div className={`${styles.pageTitleWrap}`}>
            <Typography display="inline" className={`${styles.pageTitle}`}>Knowledge Base Category</Typography>
            <nav aria-label="breadcrumb">
                <ol className={`${styles.breadcrumb}`}>
                    <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Resources</li>
                    <li className={`${styles.breadcrumbItem} ${styles.active}`}>Knowledge Base Category</li>
                </ol>
            </nav>
        </div>

        <div className={`${styles.BodyCon}`}>
            <div className={`${styles.mainCardDiv}`}>
                <div className={`${styles.mainCard}`}>
                    <div className="control-pane">
                        {contentLoading && (<div className="Loader TableLoader"><TableLoader /></div>)}

                        {!contentLoading && (<div className={`${styles.Row}`}>
                            <ArticleCategoryFilter setFilterData={setFilterData.bind(this)} setShowAddForm={setShowAddForm.bind(this)} />

                            <div className={`${styles.NotesRow}`}>
                                {categoryList.map((item) => {
                                    return (<ArticleCategoryCard key={item?.id} item={item} setShowdeletePopup={setShowdeletePopup.bind(this)} setSelectecId={setSelectecId.bind(this)} />);
                                })}

                                {categoryList.length > 0 && (<div className="Pagination TablePaginationNew List">
                                    <TablePagination
                                        count={totalCount}
                                        color="primary"
                                        page={page}
                                        rowsPerPage={perPage}
                                        onPageChange={(e, v) => setPage(v)}
                                        rowsPerPageOptions={[
                                            { label: '4', value: 4 },
                                            { label: '8', value: 8 },
                                            { label: '16', value: 16 },
                                            { label: '50', value: 50 },
                                        ]}
                                        onRowsPerPageChange={(e) => {
                                            setPage(0);
                                            setPerPage(e.target.value);
                                        }}
                                        labelRowsPerPage={'KB Category per Page:'}
                                    />
                                </div>)}

                                {!contentLoading && categoryList.length === 0 && loadError && (<div className={`${styles.NoDataMain}`}>
                                    <p className={`${styles.NoDataText}`}>Something went wrong</p>
                                    <p className={`${styles.NoDataTextSub}`}>We couldn't load categories. Please refresh the page to try again.</p>
                                </div>)}

                                {!contentLoading && categoryList.length === 0 && !loadError && (<div className={`${styles.NoDataMain}`}>
                                    <div className={`${styles.NoDataIMG}`}>
                                        <img src="/static/img/category-kb.png" alt="No Data" />
                                    </div>
                                    <p className={`${styles.NoDataText}`}>Oops!</p>
                                    <p className={`${styles.NoDataTextSub}`}>It seems we can't find that category.</p>
                                </div>)}
                            </div>
                        </div>)}
                    </div>
                </div>
            </div>
        </div>

        <Dialog
            open={showAddForm}
            onClose={(e) => setShowAddForm(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="NewPopUp MobileForm"
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle id="alert-dialog-title">
                <button className={`${styles.BackAddBU}`} onClick={(e) => setShowAddForm(false)}><ArrowLeft /></button>
                New Category
                <button onClick={(e) => setShowAddForm(false)}><X /></button>
            </DialogTitle>
            <DialogContent>
                <ArticleCategoryAdd setShowAddForm={setShowAddForm.bind(this)} setLoading={setLoading.bind(this)} onAdd={onAdd.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMsg={setSnackbarMsg.bind(this)} isEdit={false} selectecId={0} />
            </DialogContent>
        </Dialog>

        <Dialog
            open={showdeletePopup}
            onClose={(e) => setShowdeletePopup(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="NewPopUp"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="alert-dialog-title">
                Are you sure you want to delete the category?
                <button onClick={(e) => setShowdeletePopup(false)}><X /></button>
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    <div className="ModalFormGroup">
                        <label className="PopupBodyText">This category will be removed immediately. You can't undo this action.</label>
                    </div>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <button className="CancelPopupBU" onClick={(e) => confirmDelete()}>Delete</button>
                <button onClick={(e) => setShowdeletePopup(false)} className="SubmitPopupBU">Cancel</button>
            </DialogActions>
        </Dialog>

        <Snackbar
            open={snackbarOpen}
            onClose={(e) => setSnackbarOpen(false)}
            TransitionComponent={Slide}
            message={snackbarMsg}
            autoHideDuration={10000}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            action={
                <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={(e) => setSnackbarOpen(false)}
                >
                    <X />
                </IconButton>
            }
        />

        <Footer />
    </React.Fragment>);
};

export default withCustomAuthenticationRequired(ArticleCategoryList, {
    onRedirecting: () => <Loader />,
});
